import EditIcon from '@mui/icons-material/Edit';
import {
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {map} from 'rxjs';

import {clientsService} from '@store/clients';
import {
  ClientOrganisationActiveStat,
  ContactType,
  Currency,
  Domain,
  OrganisationKind,
  OrganisationLanguage,
  PaymentTerm,
  TaxRule
} from '@store/common/organisation.enums';
import {InvoicingSource, ManageContact, ManageContactType, ManageOrganisation} from '@store/common/organisation.model';

import {Add, Delete} from '@icons/index';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import AsyncSelectWithSearchComponent from '@components/inputs/asyncSelect/AsyncSelectWithSearch.component';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import MultiDatePickerComponent from '@components/inputs/multiDatePicker/MultiDatePicker.component';
import SelectComponent from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';

import MultiSelectComponent from '@components/inputs/multiSelect/MultiSelect.component';
import DeleteAddressModal from '@components/unitDetails/deleteAddress/DeleteAddress.modal';
import DeleteContactModal from '@components/unitDetails/deleteContact/DeleteContact.modal';
import {Colors} from '@constants/colors.constants';
import {Sizes} from '@constants/sizes.constants';
import {useObservable} from '@ngneat/react-rxjs';
import {
  ClientContact,
  ClientDetails,
  clientDetailsQuery,
  clientDetailsService,
  ClientInvoiceAddress
} from '@store/clients/details';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import {supplierDetailsService} from '@store/suppliers/details';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {TableFilterType} from '@store/common/tableFilter.model';

interface HeadOfficeFormProps {
  organisation?: ManageOrganisation;
  handleCancel?: () => void;
  handleDeleteContact?: (type: ManageContactType, organisationId: number | string, contactId?: number | string, successCallback?: () => void) => void;
  handleManageContact?: (manageContact: ManageContact | boolean) => void;
  handleManageAddress?: (manageAddress: ClientInvoiceAddress | boolean) => void;
  handleDeleteAddress?: (clientInvoiceAddressId?: number | string, successCallback?: () => void) => void;
  handleFacturationMode?: (facturationMode: FacturationMode) => void;
  handleSave: (client: ManageOrganisation) => void;
  isSupplier?: boolean;
  loading?: boolean;
  moreOptions?: JSX.Element[];
  readOnly?: boolean;
}

const HeadOfficeForm = (props: HeadOfficeFormProps) => {
  const {
    organisation: organisationProps,
    handleCancel,
    handleDeleteContact,
    handleManageContact,
    handleFacturationMode,
    handleSave,
    isSupplier,
    loading,
    readOnly,
    handleDeleteAddress,
    handleManageAddress,
  } = props;

  const {t} = useTranslation();

  const [showDeleteContact, setShowDeleteContact] = useState<ManageContact | false>(false);
  const [showDeleteAddress, setShowDeleteAddress] = useState<ClientInvoiceAddress | false>(false);
  const {enqueueSnackbar} = useSnackbar();

  // const [openBillingMethod, setOpenBillingMethod] = useState<ManageBillingMethod | boolean>(false);
  const [organisation, setOrganisation] = useState<ManageOrganisation>(organisationProps || {});
  const [contactClient, setContactClient] = useState<ClientContact[]>();
  const [contactToDeleteType, setContactToDeleteType] = useState<ManageContactType>(ManageContactType.ORGANISATION);
  const [contactToDeleteOrga, setContactToDeleteOrga] = useState<number | undefined>(undefined);
  const [{headOffice}] = useObservable(clientDetailsQuery.clientDetails$);

  const updateOrganisation = (name: string) => (value: any) => setOrganisation((state) => ({...state, [name]: value}));
  const updateOrganisationAddress = (name: string) => (value: any) => setOrganisation((state) => ({
    ...state,
    address: {...state.address, [name]: value}
  }));

  useEffect(() => {
    setOrganisation(organisationProps || {});
  }, [organisationProps]);

  useEffect(() => {
    if ((organisation.defaultLanguage as any) === '0') updateOrganisation('defaultLanguage')(OrganisationLanguage.ENGLISH);
  }, [organisation.defaultLanguage]);

  useEffect(() => {
    if (organisation.id !== undefined) {
      if (isSupplier) {
        supplierDetailsService.getContacts(organisation.id).pipe()
          .subscribe({
            next: (contacts) => {
              setContactClient(contacts.data);
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
      } else {
        clientDetailsService.getContacts(organisation.id).pipe()
          .subscribe({
            next: (contacts) => {
              setContactClient(contacts.data);
            },
            error: (err) => enqueueSnackbar(err.text, err.options),
          });
      }
    }

  }, [headOffice, enqueueSnackbar, organisation.id, isSupplier]);

  const errors = !readOnly ? {
    name: getFieldError(organisation.name, FieldValidationType.REQUIRED_STRING),
    address: {
      street: getFieldError(organisation.address?.street, FieldValidationType.REQUIRED_STRING),
      zip: getFieldError(organisation.address?.zip, FieldValidationType.REQUIRED_STRING),
      city: getFieldError(organisation.address?.city, FieldValidationType.REQUIRED_STRING),
      country: getFieldError(organisation.address?.country, FieldValidationType.REQUIRED_STRING),
    },
    rcNumber: getFieldError(organisation.rcNumber, FieldValidationType.REQUIRED_STRING),
    region: getFieldError(organisation.region, FieldValidationType.REQUIRED_STRING),
    currency: getFieldError(organisation.currency, FieldValidationType.REQUIRED_STRING),
    inPaymentTermsNumDays: !isSupplier ? getFieldError(organisation.inPaymentTermsNumDays, FieldValidationType.REQUIRED_STRING) : undefined,
    outPaymentTermsNumDays: isSupplier ? getFieldError(organisation.outPaymentTermsNumDays, FieldValidationType.REQUIRED_STRING) : undefined,
  } : {};

  const facturationMode = !isSupplier && (organisation as ClientDetails) ? (organisation as ClientDetails).facturationMode : undefined;
  const invoiceAddresses = !isSupplier && (organisation as ClientDetails) ? (organisation as ClientDetails).clientInvoiceAddresses || [] : undefined;

  return (
    <>
      <Stack overflow="auto" py="20px" className="scrollable" flex={1}>
        <Stack component={Paper} elevation={0} p="20px" pt={!organisationProps ? '20px' : '10px'} flex={1}>
          <Stack
            direction="row" alignItems="center" justifyContent="space-between"
            mb={!organisationProps ? '20px' : '10px'}>
            <Stack direction="row" spacing={2}>
              <Typography fontSize={20} fontWeight="700">
                {t('unitDetails.title.organisation')}
              </Typography>
              <Chip color={InvoicingSource.getColor(organisation.invoicingSource)}
                    label={InvoicingSource.getLabel(organisation.invoicingSource)}/>
            </Stack>

            <Stack direction="row" spacing={2}>
              {!readOnly && (
                <Stack direction="row" spacing={2} justifyContent="center">
                  {!!handleCancel && (
                    <Button variant="outlined" color="primary" disabled={loading} onClick={handleCancel}>
                      {t('global.cancel')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={loading || checkIfErrors(errors)}
                    onClick={() => handleSave(organisation)}>
                    {t('global.save')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{borderColor: Colors.background}}/>
          <Stack direction="row" py="20px" spacing={7}>
            <Stack flex={1} spacing={3}>
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.administrativeInformation.title')}
              </Typography>
              <Stack spacing={2}>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.companyName')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('name')}
                  value={organisation.name || ''}
                  error={errors.name}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.marketingName')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('commercialName')}
                  value={organisation.commercialName || ''}
                  readOnly={readOnly}/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.address')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisationAddress('street')}
                  value={organisation.address?.street || ''}
                  error={errors.address?.street}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.zipCode')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisationAddress('zip')}
                  value={organisation.address?.zip || ''}
                  error={errors.address?.zip}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.city')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisationAddress('city')}
                  value={organisation.address?.city || ''}
                  error={errors.address?.city}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.country')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisationAddress('country')}
                  value={organisation.address?.country || ''}
                  error={errors.address?.country}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.RCNumber')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('rcNumber')}
                  value={organisation.rcNumber || ''}
                  error={errors.rcNumber}
                  readOnly={readOnly}
                  required/>
                <MultiSelectComponent
                  labelWidth={120}
                  readOnly={false}
                  color="primary"
                  forceBackgroundColor={true}
                  label={t('unitDetails.administrativeInformation.typology')}
                  placeholder={t('global.selectOptions')}
                  handleChange={updateOrganisation('organisationKinds')}
                  renderValue={(selected) => {
                    if ((selected?.length <= 0 || selected[0].toString() === '')) {
                      return <Typography color={Colors.textWithOpacity}>{t('global.selectOptions')}</Typography>;
                    }
                    const selectedLabels = selected.map((value) => {
                      const selectedItem = OrganisationKind.selectItems().find((item) => item.value === value);
                      return selectedItem ? selectedItem.label : '';
                    });

                    return selectedLabels.join(', ');
                  }}
                  forceRenderValue={true}
                  values={organisation?.organisationKinds ?? undefined}
                  items={OrganisationKind.selectItems().map((sk) => {
                    return {value: sk.value, label: sk.label};
                  })}/>
                {isSupplier && (
                  <MultiDatePickerComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    label={t('unitDetails.contactInformation.extraordinaryClosingDays')}
                    placeholder={t('global.selectDates')}
                    handleChange={updateOrganisation('closingDays')}
                    values={organisation.closingDays}
                    readOnly={readOnly}/>
                )}
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.invoicingAdditionalInformation')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('invoicingAdditionalInformation')}
                  value={organisation.invoicingAdditionalInformation || ''}
                  readOnly={readOnly}
                />
                <CheckboxComponent
                  labelWidth={125}
                  label={t(`unitDetails.administrativeInformation.${isSupplier ? 'alsoClient' : 'alsoServiceProvider'}`)}
                  handleChange={updateOrganisation('isClientOrSupplierToo')}
                  value={organisation.isClientOrSupplierToo}
                  readOnly={!!organisationProps}/>
              </Stack>
            </Stack>
            <Stack flex={1} spacing={3}>
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.contactInformation.title')}
              </Typography>
              <Stack spacing={2}>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.region')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('region')}
                  value={organisation.region || ''}
                  error={errors.region}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.phone')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('phone')}
                  value={organisation.phone || ''}
                  readOnly={readOnly}/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.FAX')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('fax')}
                  value={organisation.fax || ''}
                  readOnly={readOnly}/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.email')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('email')}
                  value={organisation.email || ''}
                  readOnly={readOnly}
                />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.webSite')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('webSite')}
                  value={organisation.webSite || ''}
                  readOnly={readOnly}/>
                <MultiSelectComponent
                  labelWidth={120}
                  color="primary"
                  readOnly={false}
                  forceBackgroundColor={true}
                  label={t('unitDetails.contactInformation.activityArea')}
                  placeholder={t('global.selectOptions')}
                  handleChange={updateOrganisation('domains')}
                  renderValue={(selected) => {
                    if ((selected?.length <= 0 || selected[0].toString() === '')) {
                      return <Typography color={Colors.textWithOpacity}>{t('global.selectOptions')}</Typography>;
                    }
                    const selectedLabels = selected.map((value) => {
                      const selectedItem = Domain.selectItems().find((item) => item.value === value);
                      return selectedItem ? selectedItem.label : '';
                    });

                    return selectedLabels.join(', ');
                  }}
                  forceRenderValue={true}
                  values={organisation?.domains ?? undefined}
                  items={Domain.selectItems().map((sk) => {
                    return {value: sk.value, label: sk.label};
                  })}/>
                <SelectComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.communicationLanguage')}
                  placeholder={t('global.selectOption')}
                  handleChange={updateOrganisation('defaultLanguage')}
                  value={(organisation.defaultLanguage as any) === '0' ? OrganisationLanguage.ENGLISH : organisation.defaultLanguage}
                  items={OrganisationLanguage.selectItems()}
                  readOnly={readOnly}/>
                <MultiSelectComponent
                  labelWidth={120}
                  color="primary"
                  readOnly={false}
                  forceBackgroundColor={true}
                  label={t('unitDetails.contactInformation.enabledStats')}
                  placeholder={t('global.selectOptions')}
                  handleChange={updateOrganisation('enabledStats')}
                  renderValue={(selected) => {
                    if ((selected?.length <= 0 || selected[0].toString() === '')) {
                      return <Typography color={Colors.textWithOpacity}>{t('global.selectOptions')}</Typography>;
                    }
                    const selectedLabels = selected.map((value) => {
                      const selectedItem = ClientOrganisationActiveStat.selectItems().find((item) => item.value === value);
                      return selectedItem ? selectedItem.label : '';
                    });

                    return selectedLabels.join(', ');
                  }}
                  forceRenderValue={true}
                  values={organisation?.enabledStats ?? undefined}
                  items={ClientOrganisationActiveStat.selectItems().map((sk) => {
                    return {value: sk.value, label: sk.label};
                  })}/>
              </Stack>
            </Stack>
            <Stack flex={1} spacing={3}>
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.payment.title')}
              </Typography>
              <Stack spacing={2}>
                <SelectComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.currency')}
                  placeholder={t('global.selectOption')}
                  handleChange={updateOrganisation('currency')}
                  value={organisation.currency || ''}
                  items={Currency.selectItems()}
                  error={errors.currency}
                  readOnly={readOnly}
                  required/>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.accountingCode')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('companyIde')}
                  value={organisation.companyIde || ''}
                  readOnly={readOnly}/>
                <SelectComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.taxScheme')}
                  placeholder={t('global.selectOption')}
                  handleChange={(taxRule) => {
                    setOrganisation((state) => ({...state, inTaxRule: taxRule, outTaxRule: taxRule}));
                  }}
                  value={organisation[isSupplier ? 'outTaxRule' : 'inTaxRule']}
                  items={TaxRule.selectItems()}
                  readOnly={readOnly}/>
                <SelectComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.paymentChoice')}
                  placeholder={t('global.selectOption')}
                  handleChange={(paymentTerms) => {
                    setOrganisation((state) => ({
                      ...state,
                      inPaymentTermsNumDays: paymentTerms,
                      outPaymentTermsNumDays: paymentTerms
                    }));
                  }}
                  value={organisation[isSupplier ? 'outPaymentTermsNumDays' : 'inPaymentTermsNumDays']}
                  items={PaymentTerm.selectItems()}
                  error={errors[isSupplier ? 'outPaymentTermsNumDays' : 'inPaymentTermsNumDays']}
                  readOnly={readOnly}
                  required/>
                {!isSupplier && (
                  <AsyncSelectWithSearchComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    label={t('unitDetails.payment.customerToInvoice')}
                    placeholder={t('global.selectOption')}
                    handleChange={(client) => {
                      updateOrganisation('inBillingToOrganisation')({
                        id: client.value,
                        name: client.label,
                      });
                    }}
                    value={organisation.inBillingToOrganisation?.id ? {
                      label: organisation.inBillingToOrganisation.name,
                      value: organisation.inBillingToOrganisation.id
                    } : undefined}
                    getOptions={(search) => clientsService.searchClients(search).pipe(
                      map((options) => options.data.map((o) => ({label: o.name || '-', value: o.id}))),
                    )}
                    readOnly={readOnly}/>
                )}
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.IBAN')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateOrganisation('iban')}
                  value={organisation.iban || ''}
                  readOnly={readOnly}
                />
                <AsyncSelectWithSearchComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.payment.billingManager')}
                  placeholder={t('global.selectOption')}
                  handleChange={updateOrganisation('selectedBillingManager')}
                  value={organisation.selectedBillingManager}
                  getOptions={(search) => filtersService.getFilters(TableFilterType.USER, FilterBaseEntityEnum.NONE, [], search)
                    .pipe(map((result) => result.map((value) => ({value: value.id, label: value.name}))))
                  }
                  readOnly={readOnly}
                />
              </Stack>
            </Stack>
          </Stack>
          {!!organisationProps && (
            <Stack direction="row" spacing={9}>
              {!!facturationMode && (
                <Stack flex={0.33}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography fontSize={18} fontWeight="700">
                      {t('unitDetails.billingMethod.title')}
                    </Typography>
                  </Stack>
                  <List>
                    <ListItem
                      key={facturationMode.id}
                      sx={{borderBottomColor: Colors.background}}
                      disablePadding disableGutters
                      secondaryAction={!readOnly ? (
                        <IconButton onClick={() => handleFacturationMode?.(facturationMode)}>
                          <EditIcon/>
                        </IconButton>
                      ) : undefined}>
                      <ListItemButton disableGutters onClick={() => handleFacturationMode?.(facturationMode)}>
                        <ListItemText
                          primary={facturationMode.name}
                          primaryTypographyProps={{color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400'}}/>
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Stack>
              )}
              <Stack flex={0.33}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontSize={18} fontWeight="700">
                    {t('unitDetails.invoiceAddresses.title')}
                  </Typography>
                  {!readOnly && !!handleManageAddress && (
                    <IconButton
                      aria-label="medium-square-primary"
                      centerRipple={false}
                      onClick={() => handleManageAddress(true)}>
                      <Add/>
                    </IconButton>
                  )}
                </Stack>
                <List>
                  {invoiceAddresses?.length ? invoiceAddresses.sort((ad1, ad2) => (ad1.addressCode || '').localeCompare(ad2.addressCode || '')).map((address, index) => (
                    <ListItem
                      key={address.clientInvoiceAddressId}
                      divider={index < (invoiceAddresses?.length || 0) - 1}
                      sx={{borderBottomColor: Colors.background}}
                      disablePadding disableGutters
                      secondaryAction={!readOnly ? (
                        <IconButton onClick={() => {
                          setShowDeleteAddress(address);
                        }}>
                          <Delete/>
                        </IconButton>
                      ) : undefined}>
                      <ListItemButton disableGutters onClick={() => {
                        handleManageAddress?.(address);
                      }}>
                        <ListItemText
                          primary={[address.addressCode, [address.name || '', address.street || '', address.street2 || '', address.street3 || '', address.zip, address.city].filter(a => a !== '').join(', ')].join(' - ')}
                          primaryTypographyProps={{color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400'}}/>
                      </ListItemButton>
                    </ListItem>
                  )) : (
                    <ListItem disablePadding disableGutters>
                      <ListItemText
                        primary={t('global.default')}
                        primaryTypographyProps={{color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400'}}/>
                    </ListItem>
                  )}
                </List>
              </Stack>
              {<Stack flex={0.33}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontSize={18} fontWeight="700">
                    {t('unitDetails.contacts.title')}
                  </Typography>
                  {!readOnly && !!handleManageContact && (
                    <IconButton
                      aria-label="medium-square-primary"
                      centerRipple={false}
                      onClick={() => {
                        handleManageContact(true);
                      }}>
                      <Add/>
                    </IconButton>
                  )}
                </Stack>
                <List>
                  {contactClient?.length ? contactClient.map((item, index) => (
                    <ListItem
                      key={item.id}
                      divider={index < (contactClient?.length || 0) - 1}
                      sx={{borderBottomColor: Colors.background}}
                      disablePadding disableGutters
                      secondaryAction={!readOnly ? (
                        <IconButton onClick={() => {
                          setContactToDeleteOrga(item.organisationId);
                          setContactToDeleteType(item.type);
                          setShowDeleteContact(item.contact);
                        }}>
                          <Delete/>
                        </IconButton>
                      ) : undefined}>
                      <ListItemButton disableGutters onClick={() => handleManageContact?.(item.contact)}>
                        <ListItemText
                          primary={`(${item.name}) • ${ContactType.label(item.contact.contactRole)} • ${item.contact.name || item.contact.surName ? `${item.contact.name ?? ''} ${item.contact.surName ?? ''}` : item.contact.title}`}
                          primaryTypographyProps={{color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400'}}/>
                      </ListItemButton>
                    </ListItem>
                  )) : (
                    <ListItem disablePadding disableGutters>
                      <ListItemText
                        primary={t('global.default')}
                        primaryTypographyProps={{color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400'}}/>
                    </ListItem>
                  )}
                </List>
              </Stack>}
            </Stack>
          )}
        </Stack>
      </Stack>
      {/*{!!openBillingMethod && (*/}
      {/*  <ManageBillingMethodDrawer*/}
      {/*    billingMethod={typeof openBillingMethod !== "boolean" ? openBillingMethod : undefined}*/}
      {/*    readOnly={readOnly}*/}
      {/*    handleClose={() => setOpenBillingMethod(false)}*/}
      {/*    handleAdd={(billingMethod) => {*/}
      {/*      // if (typeof openBillingMethod === "boolean") {*/}
      {/*      //   updateClientUnit("billingMethods")(*/}
      {/*      //     (clientUnit.billingMethods || []).concat({*/}
      {/*      //       ...billingMethod,*/}
      {/*      //       id: (clientUnit.billingMethods?.length || 0) + 1*/}
      {/*      //     })*/}
      {/*      //   );*/}
      {/*      // } else {*/}
      {/*      //   updateClientUnit("billingMethods")(*/}
      {/*      //     (clientUnit.billingMethods || []).map((bm) => bm.id === billingMethod.id ? billingMethod : bm)*/}
      {/*      //   );*/}
      {/*      // }*/}
      {/*      setOpenBillingMethod(false);*/}
      {/*    }}/>*/}
      {/*)}*/}
      {showDeleteAddress && (
        <DeleteAddressModal
          handleClose={() => setShowDeleteAddress(false)}
          handleDelete={() => {
            if (typeof showDeleteAddress !== 'boolean') {
              handleDeleteAddress?.(showDeleteAddress.clientInvoiceAddressId, () => setShowDeleteAddress(false));
            }
          }}/>
      )}
      {showDeleteContact && (
        <DeleteContactModal
          handleClose={() => setShowDeleteContact(false)}
          handleDelete={() => {
            if (typeof showDeleteContact !== 'boolean') {
              handleDeleteContact?.(contactToDeleteType, contactToDeleteOrga ?? 0, showDeleteContact.id, () => setShowDeleteContact(false));
            }
          }}/>
      )}
    </>
  );
};

export default HeadOfficeForm;
